/**
 * Redux state of the statistics dashboard: one block per endpoint, each loading
 * and failing on its own. Registered in the root reducer under
 * `STATISTICS_DASHBOARD_KEY` (`withReducer` on the page would close an import
 * cycle through `DashboardConfig`'s nav item).
 *
 * Stale-response guard: `pending` stores the request's `requestId` and clears
 * the block's data; `fulfilled` and `rejected` are dropped unless they belong to
 * the latest request. A response whose
 * `livemode` differs from the requested mode is rejected, never stored. So a
 * LIVE/TEST switch mid-load never shows the other mode's numbers. An aborted
 * request is dropped when superseded, and puts its block back to idle when it
 * is still the latest. `reset` returns every block to idle, or only the
 * named ones (`reset({ blocks })`).
 *
 * `today` ("reviewed today", plan Decision 7) is an operations request for
 * today only, keyed on the workflow and mode, never on the period: it is not
 * one of `PERIOD_BLOCKS`, so an invalid range or a period refetch never
 * touches it.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { MODE } from '../../../../constants/mode';
import { toStatsError } from '../../../../utils/statistics/errors';
import statisticsService from '../api/statisticsService';

export const STATISTICS_DASHBOARD_KEY = 'statisticsDashboard';

export const STATS_BLOCK = {
  OVERVIEW: 'overview',
  SERVICES: 'services',
  GEO: 'geo',
  OPERATIONS: 'operations',
  TODAY: 'today',
};

/** The blocks bound to the period filter: everything except `today`. */
export const PERIOD_BLOCKS = [
  STATS_BLOCK.OVERVIEW,
  STATS_BLOCK.SERVICES,
  STATS_BLOCK.GEO,
  STATS_BLOCK.OPERATIONS,
];

export const BLOCK_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
};

export const MODE_MISMATCH = 'MODE_MISMATCH';

export const modeMismatchError = () => ({ status: null, message: MODE_MISMATCH, field: null });

const BLOCKS = Object.values(STATS_BLOCK);

/** @returns {{ status: string, data: any, error: import('../api/types').StatsError|null, requestId: string|null }} */
export const createBlockState = () => ({
  status: BLOCK_STATUS.IDLE,
  data: null,
  error: null,
  requestId: null,
});

export const initialState = Object.fromEntries(BLOCKS.map((block) => [block, createBlockState()]));

// The service already throws a structured error; anything else (a bug, an
// unexpected throw) is normalised so the stored value is always serialisable.
const asStatsError = (err) =>
  err && typeof err === 'object' && 'status' in err && 'message' in err && 'field' in err
    ? err
    : toStatsError(err);

/**
 * One thunk per block. Argument: `{ query, mode }`, where `query` is
 * `buildQuery(filters, mode, tz)` and `mode` is the current `MODE` value.
 * `method` is the `statisticsService` call (the block id by default).
 */
const createBlockThunk = (block, method = block) =>
  createAsyncThunk(
    `${STATISTICS_DASHBOARD_KEY}/${block}`,
    async ({ query, mode }, { signal, rejectWithValue }) => {
      try {
        const data = await statisticsService[method](query, { signal });
        if (data?.livemode !== (mode === MODE.LIVE)) {
          return rejectWithValue(modeMismatchError());
        }
        return data;
      } catch (err) {
        return rejectWithValue(asStatsError(err));
      }
    }
  );

export const fetchOverview = createBlockThunk(STATS_BLOCK.OVERVIEW);
export const fetchServices = createBlockThunk(STATS_BLOCK.SERVICES);
export const fetchGeo = createBlockThunk(STATS_BLOCK.GEO);
export const fetchOperations = createBlockThunk(STATS_BLOCK.OPERATIONS);
/** "Reviewed today": the operations route for `from = to = today`. */
export const fetchToday = createBlockThunk(STATS_BLOCK.TODAY, STATS_BLOCK.OPERATIONS);

const THUNKS = {
  [STATS_BLOCK.OVERVIEW]: fetchOverview,
  [STATS_BLOCK.SERVICES]: fetchServices,
  [STATS_BLOCK.GEO]: fetchGeo,
  [STATS_BLOCK.OPERATIONS]: fetchOperations,
  [STATS_BLOCK.TODAY]: fetchToday,
};

const isLatest = (blockState, meta) => meta.requestId === blockState.requestId;

const statisticsSlice = createSlice({
  name: STATISTICS_DASHBOARD_KEY,
  initialState,
  reducers: {
    // Back to idle. Without a payload every block resets (page unmount: the
    // reducer outlives the page); with `{ blocks }` only those do (an invalid
    // range resets `PERIOD_BLOCKS` and keeps `today`).
    reset: {
      reducer: (state, { payload }) => {
        if (!payload?.blocks) return initialState;
        payload.blocks.forEach((block) => {
          if (block in state) state[block] = createBlockState();
        });
        return undefined;
      },
      prepare: (payload) => ({ payload }),
    },
  },
  extraReducers: (builder) => {
    BLOCKS.forEach((block) => {
      const thunk = THUNKS[block];

      builder
        .addCase(thunk.pending, (state, { meta }) => {
          state[block] = {
            status: BLOCK_STATUS.LOADING,
            data: null,
            error: null,
            requestId: meta.requestId,
          };
        })
        .addCase(thunk.fulfilled, (state, { payload, meta }) => {
          if (!isLatest(state[block], meta)) {
            return;
          }
          state[block].status = BLOCK_STATUS.READY;
          state[block].data = payload;
          state[block].error = null;
        })
        .addCase(thunk.rejected, (state, { payload, error, meta }) => {
          if (!isLatest(state[block], meta)) {
            return;
          }
          // An aborted latest request has no replacement: back to idle, never a
          // stuck skeleton. A superseded abort is dropped by the check above.
          if (meta.aborted) {
            state[block] = createBlockState();
            return;
          }
          state[block].status = BLOCK_STATUS.ERROR;
          state[block].data = null;
          state[block].error = payload ?? { status: null, message: error?.message ?? null, field: null };
        });
    });
  },
});

const IDLE_BLOCK = createBlockState();

const selectBlock = (block) => (state) => state[STATISTICS_DASHBOARD_KEY]?.[block] ?? IDLE_BLOCK;

/** Each selector returns the block state `{ status, data, error, requestId }`. */
export const selectors = {
  selectOverview: selectBlock(STATS_BLOCK.OVERVIEW),
  selectServices: selectBlock(STATS_BLOCK.SERVICES),
  selectGeo: selectBlock(STATS_BLOCK.GEO),
  selectOperations: selectBlock(STATS_BLOCK.OPERATIONS),
  selectToday: selectBlock(STATS_BLOCK.TODAY),
};

export const { reset } = statisticsSlice.actions;

export default statisticsSlice.reducer;
